import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import { useAuth } from "../../context/AuthContext";
import {
  getClientBatchCalendar,
  submitUnavailability,
  getMyUnavailability,
} from "../../services/clientCalendarService";
import "../../../admin_module/calendar/calendar.css";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const fmtMonth = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const buildGrid = (year, month) => {
  const first = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = Array(first).fill(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

/* ── Unavailability Modal ── */
const UnavailModal = ({ date, batchId, onClose, onSuccess }) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await submitUnavailability({ batchId, date, reason });
      onSuccess();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Submission failed.";
      setError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cal-modal-overlay" onClick={onClose}>
      <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Request Unavailability</h3>
        <p style={{ margin: "0 0 0.75rem", fontSize: "0.88rem", color: "#374151" }}>
          📅 <strong>{date}</strong>
        </p>
        <textarea
          placeholder="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error && (
          <div style={{ marginTop: "0.5rem", fontSize: "0.82rem", color: "#dc2626", background: "#fee2e2", padding: "0.5rem 0.75rem", borderRadius: 6 }}>
            {error}
          </div>
        )}
        <div className="cal-modal-actions">
          <button
            onClick={onClose}
            style={{ padding: "0.45rem 1rem", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: "0.85rem" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{ padding: "0.45rem 1rem", border: "none", borderRadius: 6, background: "#2563eb", color: "#fff", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 }}
          >
            {submitting ? "Submitting…" : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── My Requests Tab ── */
const MyRequestsTab = ({ batchId, batchMode, refreshKey }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getMyUnavailability(batchId)
      .then((res) => setRequests(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, [batchId, refreshKey]);

  if (loading) return <div style={{ padding: "1rem", color: "#9ca3af" }}>Loading…</div>;
  if (!requests.length) return <div style={{ padding: "1rem", color: "#9ca3af", textAlign: "center" }}>No requests yet.</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {requests.map((r) => (
        <div key={r.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "0.9rem 1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.5rem" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>📅 {r.date}</div>
              {r.reason && <div style={{ fontSize: "0.82rem", color: "#6b7280", marginTop: 2 }}>{r.reason}</div>}
            </div>
            <span className={`cal-status cal-status-${r.status}`}>{r.status}</span>
          </div>
          {r.status === "REJECTED" && r.adminNotes && (
            <div style={{ marginTop: "0.5rem", fontSize: "0.82rem", color: "#b91c1c", background: "#fee2e2", padding: "0.4rem 0.6rem", borderRadius: 5 }}>
              Reason: {r.adminNotes}
            </div>
          )}
          {r.status === "APPROVED" && (
            <div style={{ marginTop: "0.5rem", fontSize: "0.82rem", color: "#15803d", background: "#dcfce7", padding: "0.4rem 0.6rem", borderRadius: 5 }}>
              {batchMode === "PERSONAL"
                ? "Reschedule will be arranged by admin."
                : "You will be marked as absent for this session."}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ── Main Page ── */
const ClientBatchCalendarPage = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();

  const today = new Date();
  const [cur, setCur] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [calDays, setCalDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("calendar");
  const [modal, setModal] = useState(null); // date string
  const [refreshKey, setRefreshKey] = useState(0);

  // batchMode can be passed via location state or fetched; default to BATCH for messaging
  const batchMode = "BATCH";

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getClientBatchCalendar(batchId, fmtMonth(cur));
      setCalDays(Array.isArray(res.data) ? res.data : []);
    } finally {
      setLoading(false);
    }
  }, [batchId, cur]);

  useEffect(() => { load(); }, [load]);

  const dayMap = Object.fromEntries(calDays.map((d) => [d.date, d]));

  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const handleDayClick = (day) => {
    if (!day) return;
    const dateStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const info = dayMap[dateStr];
    if (info?.dayType !== "STUDY_DAY") return;
    if (dateStr <= todayStr) return; // only future dates
    setModal(dateStr);
  };

  const cells = buildGrid(cur.getFullYear(), cur.getMonth());

  return (
    <>
      <Navbar />
      <div style={{ maxWidth: 700, margin: "0 auto", padding: "1.5rem 1rem" }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#2563eb", fontSize: "0.88rem", marginBottom: "1rem", padding: 0 }}
        >
          ← Back
        </button>

        <h2 style={{ margin: "0 0 1rem", fontSize: "1.3rem", color: "#1f2937" }}>Batch Calendar</h2>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "0.25rem", marginBottom: "1rem", borderBottom: "2px solid #e5e7eb" }}>
          {["calendar", "my-requests"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: "0.5rem 1rem", border: "none", background: "none", cursor: "pointer",
                fontWeight: activeTab === tab ? 700 : 400,
                color: activeTab === tab ? "#2563eb" : "#6b7280",
                borderBottom: activeTab === tab ? "2px solid #2563eb" : "2px solid transparent",
                marginBottom: "-2px", fontSize: "0.88rem",
              }}
            >
              {tab === "calendar" ? "Calendar" : "My Requests"}
            </button>
          ))}
        </div>

        {activeTab === "calendar" && (
          <>
            <div className="cal-wrap">
              <div className="cal-nav">
                <button className="cal-nav-btn" onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() - 1, 1))}>‹</button>
                <h3>{cur.toLocaleString("default", { month: "long", year: "numeric" })}</h3>
                <button className="cal-nav-btn" onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() + 1, 1))}>›</button>
              </div>
              <div className="cal-dow-row">
                {DOW.map((d) => <div key={d} className="cal-dow-cell">{d}</div>)}
              </div>
              {loading ? (
                <div style={{ padding: "2rem", textAlign: "center", color: "#9ca3af" }}>Loading…</div>
              ) : (
                <div className="cal-grid">
                  {cells.map((day, i) => {
                    if (!day) return <div key={i} className="cal-cell cal-empty" />;
                    const dateStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                    const info = dayMap[dateStr];
                    const isStudy = info?.dayType === "STUDY_DAY";
                    const isHoliday = info?.dayType === "HOLIDAY";
                    const isFuture = dateStr > todayStr;
                    const isToday = dateStr === todayStr;
                    return (
                      <div
                        key={i}
                        className={`cal-cell ${isStudy ? "cal-study" : isHoliday ? "cal-holiday" : ""} ${isToday ? "cal-today" : ""}`}
                        onClick={() => handleDayClick(day)}
                        title={isStudy && isFuture ? "Click to request unavailability" : undefined}
                        style={{ cursor: isStudy && isFuture ? "pointer" : "default" }}
                      >
                        <div className="cal-day-num">{day}</div>
                        {isStudy && (
                          <span style={{ fontSize: "0.65rem", color: "#15803d" }}>
                            Study{isFuture ? " ✋" : ""}
                          </span>
                        )}
                        {isHoliday && (
                          <span style={{ fontSize: "0.65rem", color: "#b91c1c", textDecoration: "line-through" }}>Holiday</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ marginTop: "0.75rem", display: "flex", gap: "1rem", fontSize: "0.78rem", color: "#6b7280", flexWrap: "wrap" }}>
              <span><span className="cal-dot cal-dot-study" /> Study Day (✋ = click to request unavailability)</span>
              <span><span className="cal-dot cal-dot-holiday" /> Holiday</span>
            </div>
          </>
        )}

        {activeTab === "my-requests" && (
          <MyRequestsTab batchId={batchId} batchMode={batchMode} refreshKey={refreshKey} />
        )}
      </div>

      {modal && (
        <UnavailModal
          date={modal}
          batchId={batchId}
          onClose={() => setModal(null)}
          onSuccess={() => { setRefreshKey((k) => k + 1); load(); }}
        />
      )}
    </>
  );
};

export default ClientBatchCalendarPage;
